import { createTheme, MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, type ComponentType } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router";
import { ModuleExamplesFrame } from "./components/Modules";
import { examplesPath, moduleBySlug, modulePath, OLD_EXAMPLE_ROUTES, type ModuleSlug } from "./lib/modules";
import { ModuleHome } from "./pages/ModuleHome";
import { QTetrisPage } from "./pages/QTetris";
import { ReferencesPage } from "./pages/References";
import { ABOUT_PAGE, Layout } from "./components/Layout";
import { ApiError } from "./lib/api";
import { AuthProvider, useAuth } from "./lib/auth";
import { Account } from "./pages/Account";
import { DftExampleDetail, DftExamplesPage } from "./pages/DftExamples";
import { NegfExampleDetail, NegfExamplesPage } from "./pages/NegfExamples";
import { PropertiesExampleDetail, PropertiesExamplesPage } from "./pages/PropertiesExamples";
import { ExampleDetail, ExamplesPage } from "./pages/Examples";
import { Admin } from "./pages/admin/Admin";
import { Home } from "./pages/Home";
import { JobDetail, JobsPage } from "./pages/Jobs";
import { AuthError, DevLogin, Splash } from "./pages/Login";
import { ProjectDetail } from "./pages/ProjectDetail";
import { Projects } from "./pages/Projects";

/** A module's illustrative projects (the gallery, or one project's guide), under the module's trail. */
const GALLERIES: Partial<Record<ModuleSlug, [ComponentType, ComponentType]>> = {
  "dft": [DftExamplesPage, DftExampleDetail],
  "tight-binding": [ExamplesPage, ExampleDetail],
  "properties": [PropertiesExamplesPage, PropertiesExampleDetail],
  "negf": [NegfExamplesPage, NegfExampleDetail],
};

export function ModuleExamples({ detail = false }: { detail?: boolean }) {
  const { slug } = useParams();
  const pages = moduleBySlug(slug) && GALLERIES[slug as ModuleSlug];
  if (!pages) return <Navigate to={slug && moduleBySlug(slug) ? modulePath(slug as ModuleSlug) : "/"} replace />;
  const Page = pages[detail ? 1 : 0];
  return <ModuleExamplesFrame slug={slug as ModuleSlug} detail={detail}><Page /></ModuleExamplesFrame>;
}

/** /examples/<id> and the like (before the module pages): the same project under its module. */
export function OldExample({ slug }: { slug: ModuleSlug }) {
  const { id } = useParams();
  return <Navigate to={examplesPath(slug, id)} replace />;
}

export const theme = createTheme({
  primaryColor: "blue",
  fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  defaultRadius: "md",
});

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (n, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && n < 2,
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * Not logged in: "/" shows the landing page (the static web/public/about.html), "/login" and every other address the
 * login (Keycloak; development: the e-mail form), after which "/login" leads to the user's home page.
 */
function Gate() {
  const { ready, loggedIn, mode, error, login } = useAuth();
  const { pathname } = useLocation();
  const landing = pathname === "/";
  const leaving = ready && !loggedIn && !error && (landing || mode === "oidc");
  useEffect(() => {
    if (!leaving) return;
    if (landing) window.location.replace(ABOUT_PAGE);
    else login(pathname === "/login" ? "/" : undefined);
  }, [leaving, landing, pathname, login]);
  if (error) return <AuthError message={error} />;
  if (!ready) return <Splash />;
  if (!loggedIn) return leaving ? <Splash text={landing ? "Loading…" : "Redirecting to login…"} /> : <DevLogin />;
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/jobs/:id" element={<JobDetail />} />
        <Route path="/account" element={<Account />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/m/:slug" element={<ModuleHome />} />
        <Route path="/m/:slug/examples" element={<ModuleExamples />} />
        <Route path="/m/:slug/examples/:id" element={<ModuleExamples detail />} />
        {Object.entries(OLD_EXAMPLE_ROUTES).flatMap(([old, slug]) => [
          <Route key={old} path={old} element={<Navigate to={examplesPath(slug)} replace />} />,
          <Route key={`${old}/:id`} path={`${old}/:id`} element={<OldExample slug={slug} />} />,
        ])}
        <Route path="/qtetris" element={<QTetrisPage />} />
        <Route path="/references" element={<ReferencesPage />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </Layout>
  );
}

const queryClient = makeQueryClient();

export function App() {
  return (
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <Notifications position="top-right" />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Gate />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </MantineProvider>
  );
}
